import { apiFetch } from '../api/client'
import type { IssueUploadBody } from '../api/types.gen'
import { api } from '../api/client'
import type { QuotaSnapshot, SignedPartsResponse, UploadTicket } from './types'

/**
 * Por dónde habla el uploader con el backend.
 *
 * El núcleo de subida —reanudación, partes, concurrencia, reintentos— es el
 * mismo suba quien suba. Lo único que cambia entre el estudio y un cliente con
 * un enlace de recepción es la credencial y la ruta.
 *
 * Antes eso estaba importado directamente: `upload/api.ts` traía el cliente
 * autenticado y con eso el uploader quedaba atado a que hubiera una sesión de
 * usuario. Reutilizarlo en el portal habría obligado a duplicarlo entero. Se
 * inyecta, y el uploader deja de saber quién es quien sube.
 */
export interface UploadRequestFile {
  filename: string
  size_bytes: number
  mime_type: string
  folder_id?: string | null
}

export interface SessionState {
  session_id: string
  asset_id: string
  project_id: string
  status: string
  strategy: 'single' | 'multipart'
  part_size_bytes: number | null
  total_parts: number | null
  total_size_bytes: number
  expires_at: string
  resumable: boolean
}

export interface CompletedAsset {
  id: string
  filename: string
  size_bytes: number
  status: string
}

export interface UploadTransport {
  /**
   * Tickets para TODO el lote en una llamada.
   *
   * Por lotes y no por archivo: con el backend en US East y los usuarios en
   * Lima (~55-90 ms de RTT), pedir firma archivo a archivo para 400 fotos
   * serían ~32 s de latencia pura antes de transferir un solo byte.
   */
  issueTickets(files: UploadRequestFile[]): Promise<{
    tickets: UploadTicket[]
    quota: QuotaSnapshot | null
  }>

  signParts(sessionId: string, from: number, to: number): Promise<SignedPartsResponse>

  getSession(sessionId: string): Promise<SessionState>

  completeUpload(
    sessionId: string,
    parts: Array<{ part_number: number; etag: string }>,
  ): Promise<CompletedAsset>

  abortUpload(sessionId: string): Promise<void>
}

/** Sube un miembro del estudio, con su sesión y dentro de un proyecto suyo. */
export function studioTransport(projectId: string): UploadTransport {
  return {
    async issueTickets(files) {
      const response = await api.post<{ data: UploadTicket[]; quota: QuotaSnapshot | null }, IssueUploadBody>(
        `/projects/${projectId}/uploads`,
        { files },
      )

      return { tickets: response.data, quota: response.quota }
    },

    async signParts(sessionId, from, to) {
      const response = await api.post<{ data: SignedPartsResponse }>(
        `/uploads/${sessionId}/parts`,
        { from, to },
      )

      return response.data
    },

    async getSession(sessionId) {
      const response = await api.get<{ data: SessionState }>(`/uploads/${sessionId}`)

      return response.data
    },

    async completeUpload(sessionId, parts) {
      const response = await api.post<{ data: CompletedAsset }>(
        `/uploads/${sessionId}/complete`,
        { parts },
      )

      return response.data
    },

    async abortUpload(sessionId) {
      await api.post(`/uploads/${sessionId}/abort`)
    },
  }
}

/**
 * Sube un cliente por un enlace de recepción, sin cuenta.
 *
 * Dos diferencias con el de arriba, y las dos vienen del backend:
 *
 *  - **No lleva `projectId`.** Lo pone el enlace. Si viajara desde aquí, quien
 *    tuviera un enlace de recepción escribiría en cualquier proyecto del
 *    estudio cambiando un UUID.
 *  - **No devuelve cuota.** Es el plan del estudio, y no es asunto del cliente
 *    final: enseñarle cuánto le queda a otro no le sirve de nada y le cuenta
 *    algo que no le corresponde.
 */
export function shareTransport(sessionToken: string): UploadTransport {
  const auth = { authorization: `Share ${sessionToken}` }

  return {
    async issueTickets(files) {
      const response = await apiFetch<{ data: UploadTicket[] }>('/public/share/uploads', {
        method: 'POST',
        body: { files },
        ...auth,
      })

      return { tickets: response.data, quota: null }
    },

    async signParts(sessionId, from, to) {
      const response = await apiFetch<{ data: SignedPartsResponse }>(
        `/public/share/uploads/${sessionId}/parts`,
        { method: 'POST', body: { from, to }, ...auth },
      )

      return response.data
    },

    async getSession(sessionId) {
      const response = await apiFetch<{ data: SessionState }>(
        `/public/share/uploads/${sessionId}`,
        auth,
      )

      return response.data
    },

    async completeUpload(sessionId, parts) {
      const response = await apiFetch<{ data: CompletedAsset }>(
        `/public/share/uploads/${sessionId}/complete`,
        { method: 'POST', body: { parts }, ...auth },
      )

      return response.data
    },

    async abortUpload(sessionId) {
      await apiFetch(`/public/share/uploads/${sessionId}/abort`, { method: 'POST', ...auth })
    },
  }
}
